import { ReactNode, SVGProps } from "react";

type IconProps = SVGProps<SVGSVGElement> & {
  size?: number;
  d?: string;
  children?: ReactNode;
};

const Icon = ({ d, children, size = 16, ...p }: IconProps) => (
  <svg
    viewBox="0 0 24 24"
    width={size}
    height={size}
    fill="none"
    stroke="currentColor"
    strokeWidth="1.5"
    strokeLinecap="round"
    strokeLinejoin="round"
    {...p}
  >
    {d ? <path d={d} /> : children}
  </svg>
);

export type IconComp = (p?: Omit<IconProps, "d" | "children">) => JSX.Element;

export const Icons: Record<string, IconComp> = {
  Home: (p) => (
    <Icon {...p}>
      <path d="M3 11.5 12 4l9 7.5" />
      <path d="M5 10v10h14V10" />
    </Icon>
  ),
  Users: (p) => (
    <Icon {...p}>
      <circle cx="9" cy="8" r="3.5" />
      <path d="M2 20c1-3.5 4-5 7-5s6 1.5 7 5" />
      <circle cx="17" cy="6" r="2.5" />
      <path d="M22 16c-.6-2.2-2.4-3.5-5-3.5" />
    </Icon>
  ),
  List: (p) => (
    <Icon {...p}>
      <path d="M8 6h13M8 12h13M8 18h13" />
      <circle cx="4" cy="6" r="1" />
      <circle cx="4" cy="12" r="1" />
      <circle cx="4" cy="18" r="1" />
    </Icon>
  ),
  Key: (p) => (
    <Icon {...p}>
      <circle cx="8" cy="14" r="4" />
      <path d="M11 13 22 2" />
      <path d="m18 6 3 3" />
      <path d="m15 9 3 3" />
    </Icon>
  ),
  Shield: (p) => (
    <Icon {...p}>
      <path d="M12 3 4 6v6c0 5 3.5 8 8 9 4.5-1 8-4 8-9V6l-8-3z" />
      <path d="m9 12 2 2 4-4" />
    </Icon>
  ),
  Brush: (p) => (
    <Icon {...p}>
      <path d="M14 3 21 10 11 20l-7 1 1-7Z" />
      <path d="m14 7 3 3" />
    </Icon>
  ),
  Window: (p) => (
    <Icon {...p}>
      <rect x="3" y="4" width="18" height="16" rx="2" />
      <path d="M3 9h18" />
      <circle cx="6" cy="6.5" r=".5" />
      <circle cx="8" cy="6.5" r=".5" />
    </Icon>
  ),
  Team: (p) => (
    <Icon {...p}>
      <circle cx="9" cy="9" r="3.5" />
      <circle cx="17" cy="8" r="2.5" />
      <path d="M2 19c1-3.5 4-5 7-5s6 1.5 7 5" />
      <path d="M22 18c-.5-2.5-2-3.5-4-3.5" />
    </Icon>
  ),
  Card: (p) => (
    <Icon {...p}>
      <rect x="3" y="5" width="18" height="14" rx="2" />
      <path d="M3 10h18" />
      <path d="M7 15h3" />
    </Icon>
  ),
  Cog: (p) => (
    <Icon {...p}>
      <circle cx="12" cy="12" r="3" />
      <path d="M12 2v3M12 19v3M4.2 4.2l2.1 2.1M17.7 17.7l2.1 2.1M2 12h3M19 12h3M4.2 19.8l2.1-2.1M17.7 6.3l2.1-2.1" />
    </Icon>
  ),
  Bell: (p) => (
    <Icon {...p}>
      <path d="M6 8a6 6 0 1 1 12 0c0 5 2 6 2 6H4s2-1 2-6z" />
      <path d="M10 19a2 2 0 0 0 4 0" />
    </Icon>
  ),
  Rocket: (p) => (
    <Icon {...p}>
      <path d="M4.5 16.5c-1.5 1.3-2 5.5-2 5.5s4.2-.5 5.5-2c.8-.8.8-2.2 0-3-.8-.8-2.2-.8-3-.5" />
      <path d="M12 15 9 12c1-3 3-6 8-7 0 5-3 7-6 8z" />
      <path d="m15 9 .01-.01" />
      <path d="M14 6.5c-1.4 1.4-3 4-3 5.5L8 12c-1 .5-2 1.5-2 2" />
    </Icon>
  ),
  Plus: (p) => (
    <Icon {...p}>
      <path d="M12 5v14M5 12h14" />
    </Icon>
  ),
  Search: (p) => (
    <Icon {...p}>
      <circle cx="11" cy="11" r="7" />
      <path d="m20 20-3.5-3.5" />
    </Icon>
  ),
  Filter: (p) => (
    <Icon {...p}>
      <path d="M3 5h18l-7 9v6l-4-2v-4z" />
    </Icon>
  ),
  Download: (p) => (
    <Icon {...p}>
      <path d="M12 3v12" />
      <path d="m7 11 5 5 5-5" />
      <path d="M5 19h14" />
    </Icon>
  ),
  Copy: (p) => (
    <Icon {...p}>
      <rect x="9" y="9" width="11" height="11" rx="2" />
      <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
    </Icon>
  ),
  Check: (p) => (
    <Icon {...p}>
      <path d="m5 12 5 5L20 7" />
    </Icon>
  ),
  X: (p) => (
    <Icon {...p}>
      <path d="M6 6l12 12M18 6 6 18" />
    </Icon>
  ),
  ChevronR: (p) => (
    <Icon {...p}>
      <path d="m9 6 6 6-6 6" />
    </Icon>
  ),
  ChevronD: (p) => (
    <Icon {...p}>
      <path d="m6 9 6 6 6-6" />
    </Icon>
  ),
  ChevronL: (p) => (
    <Icon {...p}>
      <path d="m15 6-6 6 6 6" />
    </Icon>
  ),
  ArrowR: (p) => (
    <Icon {...p}>
      <path d="M5 12h14M13 5l7 7-7 7" />
    </Icon>
  ),
  TrendUp: (p) => (
    <Icon {...p}>
      <path d="m3 17 6-6 4 4 8-9" />
      <path d="M14 6h7v7" />
    </Icon>
  ),
  TrendDown: (p) => (
    <Icon {...p}>
      <path d="m3 7 6 6 4-4 8 9" />
      <path d="M14 18h7v-7" />
    </Icon>
  ),
  Dot: (p) => (
    <Icon {...p}>
      <circle cx="12" cy="12" r="2" fill="currentColor" />
    </Icon>
  ),
  Trash: (p) => (
    <Icon {...p}>
      <path d="M3 6h18" />
      <path d="M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
      <path d="m6 6 1 14a2 2 0 0 0 2 2h6a2 2 0 0 0 2-2L18 6" />
    </Icon>
  ),
  Rotate: (p) => (
    <Icon {...p}>
      <path d="M3 12a9 9 0 0 1 15-6.7L21 8" />
      <path d="M21 3v5h-5" />
      <path d="M21 12a9 9 0 0 1-15 6.7L3 16" />
      <path d="M3 21v-5h5" />
    </Icon>
  ),
  Eye: (p) => (
    <Icon {...p}>
      <path d="M2 12s4-7 10-7 10 7 10 7-4 7-10 7S2 12 2 12z" />
      <circle cx="12" cy="12" r="3" />
    </Icon>
  ),
  EyeOff: (p) => (
    <Icon {...p}>
      <path d="M3 3l18 18" />
      <path d="M10.6 6.1A10 10 0 0 1 22 12a10 10 0 0 1-3.4 4.4M6.1 6.1A10 10 0 0 0 2 12s4 7 10 7c2 0 3.8-.6 5.4-1.6" />
      <path d="M9.9 9.9a3 3 0 0 0 4.2 4.2" />
    </Icon>
  ),
  Lightning: (p) => (
    <Icon {...p}>
      <path d="m13 2-7 12h6l-1 8 7-12h-6z" />
    </Icon>
  ),
  Code: (p) => (
    <Icon {...p}>
      <path d="m9 9-4 3 4 3" />
      <path d="m15 9 4 3-4 3" />
    </Icon>
  ),
  Globe: (p) => (
    <Icon {...p}>
      <circle cx="12" cy="12" r="9" />
      <path d="M3 12h18" />
      <path d="M12 3a14 14 0 0 1 0 18M12 3a14 14 0 0 0 0 18" />
    </Icon>
  ),
  Robot: (p) => (
    <Icon {...p}>
      <rect x="4" y="8" width="16" height="11" rx="2" />
      <path d="M12 4v4" />
      <circle cx="9" cy="13" r="1" />
      <circle cx="15" cy="13" r="1" />
      <path d="M9 17h6" />
      <path d="M2 12v3M22 12v3" />
    </Icon>
  ),
  Person: (p) => (
    <Icon {...p}>
      <circle cx="12" cy="8" r="4" />
      <path d="M4 21c1.5-4 4.5-6 8-6s6.5 2 8 6" />
    </Icon>
  ),
  Spark: (p) => (
    <Icon {...p}>
      <path d="M12 3v4M12 17v4M3 12h4M17 12h4M5.6 5.6l2.8 2.8M15.6 15.6l2.8 2.8M5.6 18.4l2.8-2.8M15.6 8.4l2.8-2.8" />
    </Icon>
  ),
  Building: (p) => (
    <Icon {...p}>
      <rect x="4" y="3" width="16" height="18" rx="1" />
      <path d="M9 7h2M9 11h2M9 15h2M14 7h2M14 11h2M14 15h2" />
    </Icon>
  ),
  Book: (p) => (
    <Icon {...p}>
      <path d="M4 4a2 2 0 0 1 2-2h13v18H6a2 2 0 0 0-2 2z" />
      <path d="M4 4v16" />
    </Icon>
  ),
  Help: (p) => (
    <Icon {...p}>
      <circle cx="12" cy="12" r="9" />
      <path d="M9.5 9a2.5 2.5 0 1 1 4 2c-1 .8-1.5 1.5-1.5 2.5" />
      <circle cx="12" cy="17" r=".5" fill="currentColor" />
    </Icon>
  ),
  External: (p) => (
    <Icon {...p}>
      <path d="M14 4h6v6" />
      <path d="m20 4-9 9" />
      <path d="M19 14v5a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1V6a1 1 0 0 1 1-1h5" />
    </Icon>
  ),
  Mail: (p) => (
    <Icon {...p}>
      <rect x="3" y="5" width="18" height="14" rx="2" />
      <path d="m3 7 9 6 9-6" />
    </Icon>
  ),
  Lock: (p) => (
    <Icon {...p}>
      <rect x="5" y="11" width="14" height="10" rx="2" />
      <path d="M8 11V8a4 4 0 0 1 8 0v3" />
    </Icon>
  ),
  Fingerprint: (p) => (
    <Icon {...p}>
      <path d="M5 12a7 7 0 0 1 14 0v2" />
      <path d="M9 12a3 3 0 0 1 6 0v3a4 4 0 0 1-4 4" />
      <path d="M12 12v3M5 16c1 2 2 4 3 5M19 16c-1 2-1.5 3-2 4" />
    </Icon>
  ),
  Memory: (p) => (
    <Icon {...p}>
      <path d="M12 3a4 4 0 0 0-4 4v1a3 3 0 0 0-3 3v2a3 3 0 0 0 3 3v1a4 4 0 0 0 4 4 4 4 0 0 0 4-4v-1a3 3 0 0 0 3-3v-2a3 3 0 0 0-3-3V7a4 4 0 0 0-4-4Z" />
      <path d="M12 7v10" />
    </Icon>
  ),
  Sun: (p) => (
    <Icon {...p}>
      <circle cx="12" cy="12" r="4" />
      <path d="M12 2v2M12 20v2M4.9 4.9l1.4 1.4M17.7 17.7l1.4 1.4M2 12h2M20 12h2M4.9 19.1l1.4-1.4M17.7 6.3l1.4-1.4" />
    </Icon>
  ),
  Moon: (p) => (
    <Icon {...p}>
      <path d="M21 12.8A9 9 0 1 1 11.2 3a7 7 0 0 0 9.8 9.8Z" />
    </Icon>
  ),
  Menu: (p) => (
    <Icon {...p}>
      <path d="M3 6h18M3 12h18M3 18h18" />
    </Icon>
  ),
  Sliders: (p) => (
    <Icon {...p}>
      <path d="M4 6h10M18 6h2" />
      <circle cx="16" cy="6" r="2" />
      <path d="M4 12h2M10 12h10" />
      <circle cx="8" cy="12" r="2" />
      <path d="M4 18h12M20 18h0" />
      <circle cx="18" cy="18" r="2" />
    </Icon>
  ),
};

export function Cascade({
  size = 28,
  color = "var(--rb-orange)",
}: {
  size?: number;
  color?: string;
}) {
  const cx = 12;
  const cy = 12;
  const wedges = [
    { a1: -90, a2: -25, w: 3.0, op: 1.0, r: 9 },
    { a1: 12, a2: 62, w: 2.6, op: 0.78, r: 9 },
    { a1: 95, a2: 140, w: 2.4, op: 0.55, r: 9 },
    { a1: 175, a2: 210, w: 2.0, op: 0.38, r: 9 },
    { a1: 240, a2: 265, w: 1.6, op: 0.24, r: 9 },
    { a1: 290, a2: 310, w: 1.2, op: 0.14, r: 9 },
  ];
  const toXY = (r: number, deg: number): [number, number] => {
    const rad = (deg * Math.PI) / 180;
    return [cx + r * Math.cos(rad), cy + r * Math.sin(rad)];
  };
  const arc = (r: number, a1: number, a2: number) => {
    const [x1, y1] = toXY(r, a1);
    const [x2, y2] = toXY(r, a2);
    const large = a2 - a1 > 180 ? 1 : 0;
    return `M ${x1} ${y1} A ${r} ${r} 0 ${large} 1 ${x2} ${y2}`;
  };
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
      {wedges.map((w, i) => (
        <path
          key={i}
          d={arc(w.r, w.a1, w.a2)}
          stroke={color}
          strokeOpacity={w.op}
          strokeWidth={w.w}
          strokeLinecap="round"
          fill="none"
        />
      ))}
      <circle cx={cx} cy={cy} r="2.2" fill={color} />
    </svg>
  );
}
